import { useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for infinite scroll functionality
 * @param {Function} fetchNextPage - Function to fetch next page
 * @param {boolean} hasNextPage - Whether there are more pages to load
 * @param {boolean} isFetchingNextPage - Whether currently fetching next page
 * @param {number} threshold - Threshold for intersection observer (0-1, default: 0.1)
 * @param {string} rootMargin - Root margin for intersection observer (default: '100px')
 * @returns {Object} - Object containing triggerRef to attach to trigger element
 */
const useInfiniteScroll = ({
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  threshold = 0.1,
  rootMargin = '100px'
}) => {
  const triggerRef = useRef(null);

  const handleIntersection = useCallback((entries) => {
    const [entry] = entries;
    
    if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    const trigger = triggerRef.current;
    
    if (!trigger) return;

    const observer = new IntersectionObserver(handleIntersection, {
      rootMargin,
      threshold
    });

    observer.observe(trigger);

    return () => {
      if (trigger) {
        observer.unobserve(trigger);
      }
    };
  }, [handleIntersection, rootMargin, threshold]);

  return { triggerRef };
};

export default useInfiniteScroll;
